import React, { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { parse } from 'csv-parse/sync';
import {
  Box,
  Button,
  DataTable,
  Layer,
  Select,
  Spinner,
  Text,
  TextInput
} from 'grommet';
import { Trash, Upload } from 'grommet-icons';
import { qlikBrand } from '../../branding';
import { API } from '../../libs/amplifyAPI';
import { parseQuoteUnits } from '../../libs/parseQuoteUnits';
import configs from './configs';

const fields = [
  { property: 'product', header: 'product' },
  { property: 'orderedIn', header: 'orderedIn', options: configs.months },
  { property: 'language', header: 'language', options: configs.locales },
  { property: 'vendor', header: 'vendor', options: configs.vendors },
  { property: 'type', header: 'type' },
  { property: 'handoffName', header: 'handoffName' },
  { property: 'generatedFromMetrics', header: 'FromMetrics' },
  { property: 'managementFee', header: 'MF' },
  { property: 'actualInEUR', header: 'actual' },
  { property: 'jiraID', header: 'jira' },
  { property: 'comment', header: 'comment' },
  { property: 'mtWordCount', header: 'mtWC' },
  { property: 'mtCost', header: 'mtCost' },
  { property: 'htCost', header: 'htCost' }
];

const NUMBER_RE = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
const validators = {
  generatedFromMetrics: NUMBER_RE,
  managementFee: NUMBER_RE,
  actualInEUR: NUMBER_RE,
  mtWordCount: NUMBER_RE,
  mtCost: NUMBER_RE,
  htCost: NUMBER_RE
};

const UploadQuoteFromPdf = ({ onExit }) => {
  const fileInput = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const close = () => onExit({ showUploadPage: false });
  const hasInvalidNumbers = result?.records.some((record) =>
    Object.entries(validators).some(([property, pattern]) =>
      !pattern.test(String(record[property] ?? ''))
    )
  ) || false;

  const updateRecord = (id, property, value) => {
    setResult((current) => ({
      ...current,
      records: current.records.map((record) =>
        record.id === id
          ? {
            ...record,
            [property]: value,
            ...(property === 'orderedIn' ? { handbackDate: value } : {})
          }
          : record
      )
    }));
  };

  const removeRecord = (id) => {
    setResult((current) => ({
      ...current,
      records: current.records.filter((record) => record.id !== id)
    }));
  };

  const columns = [
    ...fields.map(({ property, header, options }) => ({
      property,
      header,
      render: (record) => options ? (
        <Select
          name={`${record.id}-${property}`}
          options={options}
          value={record[property] ?? ''}
          disabled={submitting}
          onChange={({ option }) => updateRecord(record.id, property, option)}
        />
      ) : (() => {
        const value = record[property] ?? '';
        const invalid = validators[property] && !validators[property].test(String(value));
        return (
          <Box border={invalid ? { color: 'status-error', size: 'small' } : undefined}>
            <TextInput
              name={`${record.id}-${property}`}
              value={value}
              disabled={submitting}
              aria-invalid={invalid || undefined}
              onChange={(event) => updateRecord(record.id, property, event.target.value)}
            />
          </Box>
        );
      })()
    })),
    {
      property: 'actions',
      header: '',
      render: (record) => (
        <Button
          icon={<Trash />}
          a11yTitle={`Delete ${record.language} quote`}
          disabled={submitting}
          hoverIndicator
          onClick={() => removeRecord(record.id)}
        />
      )
    }
  ];

  const onSelectFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParsing(true);
    setError('');
    setResult(null);

    try {
      const parsed = await parseQuoteUnits(await file.arrayBuffer());
      setResult({
        ...parsed,
        records: parsed.records.map((record) => ({
          id: uuidv4(),
          ...record,
          handbackDate: record.orderedIn
        }))
      });
    } catch (parseError) {
      setError(parseError.message || 'The PDF could not be parsed.');
    } finally {
      setParsing(false);
      event.target.value = '';
    }
  };

  const onUpload = async () => {
    if (!result?.records.length || hasInvalidNumbers) return;

    setSubmitting(true);
    setError('');
    try {
      const quotesToAdd = result.records.map((record) =>
        Object.fromEntries(
          Object.entries(record).map(([property, value]) => [property, String(value ?? '').replace(/,/g, '_')])
        )
      );
      const response = await API.post('Quotes', 'quotes', {
        body: { quotesToAdd }
      });
      const quoteList = parse(response.quotes, {
        columns: true,
        skip_empty_lines: true
      });
      quoteList.reverse();
      onExit({ showUploadPage: false, quoteList });
    } catch (uploadError) {
      console.log(uploadError);
      setError(uploadError.message || 'The extracted quotes could not be uploaded.');
      setSubmitting(false);
    }
  };

  return (
    <Layer
      full
      modal
      margin='none'
      position='center'
      onEsc={submitting ? undefined : close}
    >
      <Box fill overflow='auto' pad='large' gap='medium'>
        <Box direction='row' align='center' justify='between' flex={false}>
          <Text size='large' weight='bold'>Upload Quotes from PDF</Text>
          <Button label='close' disabled={submitting} onClick={close} />
        </Box>

        <Box direction='row' align='center' gap='medium' flex={false}>
          <input
            ref={fileInput}
            type='file'
            accept='application/pdf,.pdf'
            onChange={onSelectFile}
            style={{ display: 'none' }}
          />
          <Button
            primary
            icon={<Upload />}
            color={qlikBrand.flood.turquoise}
            label='Upload PDF'
            disabled={parsing || submitting}
            onClick={() => fileInput.current?.click()}
          />
          {fileName && <Text>{fileName}</Text>}
        </Box>

        {parsing && (
          <Box align='center' pad='large' gap='small'>
            <Spinner size='large' />
            <Text>Reading quote details...</Text>
          </Box>
        )}

        {error && (
          <Box background='status-error' pad='medium' round='xsmall' flex={false}>
            <Text color='white'>{error}</Text>
          </Box>
        )}

        {result && (
          <Box gap='medium' flex={false}>
            <Box gap='xsmall'>
              <Text weight='bold'>{result.records.length} quote(s) extracted</Text>
              <Text size='medium' weight='bold' color={qlikBrand.flood.turquoise}>
                Project: {result.meta.projectName || 'Not found'}
              </Text>
              <Text size='medium' weight='bold' color={qlikBrand.flood.turquoise}>
                Languages: {Object.keys(result.summary).join(', ') || 'None found'}
              </Text>
              {result.warnings.map((warning) => (
                <Text key={warning} color='status-warning' size='small'>{warning}</Text>
              ))}
              {hasInvalidNumbers && (
                <Text color='status-error' size='small'>
                  Numeric fields must contain a non-negative whole number or decimal.
                </Text>
              )}
            </Box>
            {result.records.length > 0 ? (
              <Box gap='medium'>
                <DataTable
                  columns={columns}
                  data={result.records}
                  primaryKey='id'
                  paginate
                  step={20}
                />
                <Box direction='row' justify='between' align='center' gap='small'>
                  <Button
                    label='Cancel'
                    disabled={submitting}
                    onClick={close}
                  />
                  <Box direction='row' align='center' gap='small'>
                  {submitting && <Spinner size='small' />}
                  <Button
                    primary
                    icon={<Upload />}
                    color={qlikBrand.flood.turquoise}
                    label={submitting ? 'Uploading...' : 'Upload to S3'}
                    disabled={submitting || hasInvalidNumbers}
                    onClick={onUpload}
                  />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Text>No supported quote rows were found.</Text>
            )}
          </Box>
        )}
      </Box>
    </Layer>
  );
};

export default UploadQuoteFromPdf;